import {
  ClockIcon,
  Cog6ToothIcon,
  HeartIcon,
  InboxStackIcon,
  ShieldCheckIcon,
  Squares2X2Icon,
} from '@heroicons/react/24/outline';
import { Link, NavLink } from 'react-router-dom';

import { Role } from '../auth/roles';
import type { RoleValue } from '../auth/roles';
import { usePrincipal } from '../auth/usePrincipal';
import { useTimezone } from '../time/TimezoneProvider';

interface NavItem {
  to: string;
  label: string;
  Icon: typeof Squares2X2Icon;
  /** Hidden below this role, because the route would 403 anyway. */
  minimum: RoleValue;
}

const SECTIONS: { heading: string; items: NavItem[] }[] = [
  {
    heading: 'Overview',
    items: [
      { to: '/', label: 'Dashboard', Icon: Squares2X2Icon, minimum: Role.VIEWER },
      { to: '/cases', label: 'Cases', Icon: InboxStackIcon, minimum: Role.VIEWER },
    ],
  },
  {
    heading: 'Response',
    items: [
      { to: '/approvals', label: 'Approvals', Icon: ShieldCheckIcon, minimum: Role.VIEWER },
    ],
  },
  {
    heading: 'Administration',
    items: [
      { to: '/health', label: 'Health', Icon: HeartIcon, minimum: Role.ADMIN },
      { to: '/settings', label: 'Settings', Icon: Cog6ToothIcon, minimum: Role.VIEWER },
    ],
  },
];

const BUILD_VERSION = import.meta.env.VITE_BUILD_VERSION ?? 'dev';
const BUILD_SHA = import.meta.env.VITE_BUILD_SHA ?? 'local';

export function Sidebar({ onNavigate }: { onNavigate?: () => void }) {
  const principal = usePrincipal();
  const { zone, isAuto } = useTimezone();

  return (
    <div className="flex h-full flex-col bg-surface">
      <div className="flex items-center gap-3 border-b border-line px-5 py-5">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded bg-brand">
          <ShieldCheckIcon className="h-4 w-4 text-on-brand" aria-hidden="true" />
        </div>
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-body">IRA</p>
          <p className="truncate text-xs text-muted">Incident Response</p>
        </div>
      </div>

      <nav aria-label="Main" className="flex-1 space-y-5 overflow-y-auto px-3 py-4 scroll-thin">
        {SECTIONS.map((section) => {
          const visible = section.items.filter((item) => principal.role >= item.minimum);
          if (visible.length === 0) return null;
          return (
            <div key={section.heading}>
              <p className="mb-1 px-3 text-[10px] font-semibold uppercase tracking-wider text-subtle">
                {section.heading}
              </p>
              <div className="space-y-0.5">
                {visible.map(({ to, label, Icon }) => (
                  <NavLink
                    key={to}
                    to={to}
                    end={to === '/'}
                    onClick={onNavigate}
                    className={({ isActive }) =>
                      `relative flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                        isActive
                          ? 'bg-brand-subtle text-brand-text'
                          : 'text-muted hover:bg-raised hover:text-body'
                      }`
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <span
                          aria-hidden="true"
                          className={`absolute left-0 h-6 w-0.5 rounded-r ${
                            isActive ? 'bg-brand' : 'bg-transparent'
                          }`}
                        />
                        <Icon className="h-5 w-5 shrink-0" aria-hidden="true" />
                        {label}
                      </>
                    )}
                  </NavLink>
                ))}
              </div>
            </div>
          );
        })}
      </nav>

      {/*
        The active timezone, always visible.
        Every timestamp in the app is rendered in this zone, so a reader comparing a sign-in
        time against something outside the platform needs to know which zone they are looking
        at without opening Settings. `· auto` distinguishes detected from deliberately pinned.
      */}
      <div className="border-t border-line px-4 py-3">
        <Link
          to="/settings"
          onClick={onNavigate}
          title={
            isAuto
              ? 'Display timezone, detected from this device — click to change'
              : 'Display timezone, pinned by you — click to change'
          }
          className="flex items-center gap-1 text-[11px] text-subtle hover:text-body"
        >
          <ClockIcon className="h-3 w-3 shrink-0" aria-hidden="true" />
          <span className="truncate">
            {zone}
            {isAuto ? ' · auto' : ''}
          </span>
        </Link>
        <p className="mt-1 truncate font-mono text-[11px] text-subtle">
          v{BUILD_VERSION} · {BUILD_SHA}
        </p>
      </div>
    </div>
  );
}
